import { useMemo } from "react";
import CustomTable from "../../../components/CustomTable/CustomTable";
import { useSelector } from "react-redux";
import { Tag } from "antd";
import { GLOBAL_CONSTANTS } from "../../../constants/constants";
import { formatCurrency, convertToPercentage } from "../../../helpers/helpers";
import { safeStringSort } from "../../../helpers/sortHelpers";
import {
  DEAL_CATEGORY_IDS,
  isSupplyChainCategory,
} from "../../../helpers/dealCategoryProducts";

const getDealCategoryTag = (categoryName, categoryId) => {
  const name = categoryName || "";
  const id = categoryId || "";
  const normalized = (name + " " + id).toLowerCase();

  if (normalized.includes("joint venture") || id === "DCT1000002" || normalized.includes("jv")) {
    return <Tag color="green">JV</Tag>;
  }
  if (normalized.includes("supply chain") || id === "DCT1000006" || normalized.includes("scf")) {
    return <Tag color="purple">SCF</Tag>;
  }
  if (normalized.includes("invoice") || id === "DCT1000001" || normalized.includes("if")) {
    return <Tag color="blue">IF</Tag>;
  }

  return name ? <Tag color="default">{name}</Tag> : GLOBAL_CONSTANTS.NA;
};

const EnterpriseDetails = ({
  enterpriseDetailsData,
  dealCategoryId,
  dealCategoryName,
}) => {
  const { loading, busDetailedViewData } = useSelector((state) => state?.business || {});

  const activeDealCategoryId =
    dealCategoryId || busDetailedViewData?.busDetails?.dealCategoryId;
  const activeDealCategoryName =
    dealCategoryName ||
    busDetailedViewData?.busDetails?.dealCategoryName ||
    busDetailedViewData?.busDetails?.dealCategory;

  const filteredData = useMemo(() => {
    if (!enterpriseDetailsData || !Array.isArray(enterpriseDetailsData)) {
      return [];
    }

    if (!activeDealCategoryId && !activeDealCategoryName) {
      return enterpriseDetailsData;
    }

    const isTargetScf =
      isSupplyChainCategory(activeDealCategoryId) ||
      isSupplyChainCategory(activeDealCategoryName) ||
      String(activeDealCategoryName || "").toLowerCase().includes("supply chain") ||
      String(activeDealCategoryName || "").toLowerCase().trim() === "scf";

    const isTargetIf =
      activeDealCategoryId === DEAL_CATEGORY_IDS.INVOICE_FINANCING ||
      activeDealCategoryId === "DCT1000001" ||
      String(activeDealCategoryName || "").toLowerCase().includes("invoice") ||
      String(activeDealCategoryName || "").toLowerCase().trim() === "if";

    if (!isTargetScf && !isTargetIf) {
      return enterpriseDetailsData;
    }

    return enterpriseDetailsData.filter((item) => {
      const itemCategoryId = item?.dealCategoryId;
      const itemCategoryName = String(item?.dealCategoryName || "").toLowerCase();

      const itemIsScf =
        isSupplyChainCategory(itemCategoryId) ||
        isSupplyChainCategory(itemCategoryName) ||
        itemCategoryName.includes("supply chain") ||
        itemCategoryName.trim() === "scf";

      const itemIsIf =
        itemCategoryId === DEAL_CATEGORY_IDS.INVOICE_FINANCING ||
        itemCategoryId === "DCT1000001" ||
        itemCategoryName.includes("invoice") ||
        itemCategoryName.trim() === "if";

      if (isTargetScf) {
        return itemIsScf;
      }
      if (isTargetIf) {
        return itemIsIf;
      }
      return true;
    });
  }, [enterpriseDetailsData, activeDealCategoryId, activeDealCategoryName]);

  const columns = useMemo(
    () => [
      {
        title: "Enterprise Name",
        dataIndex: "entName",
        key: "entName",
        width: 320,
        ellipsis: true,
        disableDefaultEllipsis: true,
        sorter: (a, b) => safeStringSort(a?.entName, b?.entName),
      },
      {
        title: "Category",
        dataIndex: "dealCategoryName",
        key: "dealCategoryName",
        align: "center",
        width: 110,
        render: (text, record) => getDealCategoryTag(text || record?.dealCategoryName, record?.dealCategoryId),
        sorter: (a, b) => safeStringSort(a?.dealCategoryName, b?.dealCategoryName),
      },
      {
        title: "Approved Credit",
        dataIndex: "approvedCredit",
        key: "approvedCredit",
        width: 180,
        render: (text) => (text ? formatCurrency(text) : GLOBAL_CONSTANTS.NA),
      },
      {
        title: "Utilized Credit",
        dataIndex: "usedCredit",
        key: "usedCredit",
        width: 180,
        render: (text) => (text ? formatCurrency(text) : GLOBAL_CONSTANTS.NA),
      },
      {
        title: "Interest Rate",
        dataIndex: "annualDiscountRate",
        key: "annualDiscountRate",
        width: 160,
        render: (text) => (text ? convertToPercentage(text) : GLOBAL_CONSTANTS.NA),
      },
      {
        title: "Business - Enterprise Status",
        dataIndex: "statusName",
        key: "statusName",
        width: 220,
        sorter: (a, b) => safeStringSort(a?.statusName, b?.statusName),
      },
    ],
    [],
  );

  return (
    <CustomTable
      rowKey={(record) => record?.busEntId || record?.entId || record?.entName}
      columns={columns}
      dataSource={filteredData}
      loading={loading}
    />
  );
};

export default EnterpriseDetails;
